import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import SectionCard from './ui/SectionCard';
import { useAuth } from '../AuthContext';
import api from '../api';
import tokens from '../theme/tokens';
import { formatCurrency } from '../utils/image';

const ROTULO = { goleiro: 'Goleiros', arbitro: 'Árbitros' };

// Escala de goleiros e árbitros da pelada (Admin/Diretor): substituições, valores e check-in pelo profissional.
const EscalaPelada = ({ peladaId, onMessage, sx }) => {
  const { userLogado } = useAuth();
  const [dados, setDados] = useState(null);
  const [novo, setNovo] = useState(null); // { tipo, pessoa, valor, substituir }
  const [ocupado, setOcupado] = useState(false);
  const podeGerenciar = userLogado && (userLogado.perfil === 'Admin' || userLogado.perfil === 'Diretor');

  const executar = async (fn, sucesso) => {
    setOcupado(true);
    try {
      const response = await fn();
      setDados(response.data);
      if (sucesso) onMessage && onMessage(sucesso);
      return true;
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Não foi possível atualizar a escala.', 'error');
      return false;
    } finally {
      setOcupado(false);
    }
  };

  useEffect(() => {
    if (podeGerenciar && peladaId) executar(() => api.get(`peladas/${peladaId}/escala`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peladaId, podeGerenciar]);

  if (!podeGerenciar || !peladaId || !dados) return null;

  const base = `peladas/${peladaId}/escala`;
  const naEscala = new Set(dados.escala.map((e) => `${e.tipo}-${e.pessoa}`));

  const salvarNovo = async () => {
    const { tipo, pessoa, valor, substituir } = novo;
    if (substituir) {
      const ok = await executar(() => api.remove(`${base}/${substituir._id}`));
      if (!ok) return;
    }
    const ok = await executar(() => api.post(base, { tipo, pessoa, valor }), substituir ? `${substituir.nome} substituído` : 'Adicionado à escala');
    if (ok) setNovo(null);
  };

  const opcoes = novo ? dados.disponiveis[novo.tipo].filter((p) => !naEscala.has(`${novo.tipo}-${p._id}`)) : [];

  return (
    <SectionCard
      sx={sx}
      title="Escala da pelada"
      subtitle="Goleiros e árbitros vinculados. O check-in gera o pagamento a fazer."
      icon={<GroupsRoundedIcon color="primary" />}
      actions={
        <>
          <Button size="small" startIcon={<PushPinRoundedIcon />} onClick={() => executar(() => api.post(`${base}/aplicar-fixa`), 'Escala fixa aplicada')} disabled={ocupado}>Aplicar escala fixa</Button>
          <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => setNovo({ tipo: 'goleiro', pessoa: '', valor: '' })} disabled={ocupado}>Adicionar</Button>
        </>
      }
    >
      <Grid container spacing={2}>
        {['goleiro', 'arbitro'].map((tipo) => {
          const itens = dados.escala.filter((e) => e.tipo === tipo);
          return (
            <Grid item xs={12} md={6} key={tipo}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{ROTULO[tipo]}</Typography>
              {itens.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Ninguém escalado.</Typography>
              ) : (
                <Stack spacing={1}>
                  {itens.map((item) => (
                    <Stack key={item._id} direction="row" spacing={1} alignItems="center" sx={{ p: 1.25, borderRadius: 2, border: 1, borderColor: item.checkinEm ? tokens.color.success : 'divider', bgcolor: item.checkinEm ? tokens.color.successSoft : 'transparent' }}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} noWrap>{item.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(item.valor)} ·{' '}
                          {item.checkinEm
                            ? `check-in ${new Date(item.checkinEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}${item.checkinPor !== 'próprio' ? ` (por ${item.checkinPor})` : ''}`
                            : 'aguardando check-in'}
                        </Typography>
                      </Box>
                      {item.statusPagamento === 'pago' && <Chip size="small" color="success" label="Pago" />}
                      {item.checkinEm ? (
                        item.statusPagamento !== 'pago' && (
                          <Tooltip title="Desfazer check-in (cancela o pagamento a fazer)">
                            <IconButton size="small" onClick={() => executar(() => api.remove(`${base}/${item._id}/checkin`), 'Check-in desfeito')} disabled={ocupado}><UndoRoundedIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )
                      ) : (
                        <Button size="small" variant="contained" color="success" startIcon={<HowToRegRoundedIcon />} onClick={() => executar(() => api.post(`${base}/${item._id}/checkin`), `Check-in de ${item.nome} registrado`)} disabled={ocupado}>
                          Check-in
                        </Button>
                      )}
                      {item.statusPagamento !== 'pago' && (
                        <>
                          <Tooltip title="Substituir">
                            <IconButton size="small" onClick={() => setNovo({ tipo, pessoa: '', valor: '', substituir: item })} disabled={ocupado}><SwapHorizRoundedIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Remover da escala">
                            <IconButton size="small" color="error" onClick={() => executar(() => api.remove(`${base}/${item._id}`), `${item.nome} removido da escala`)} disabled={ocupado}><PersonRemoveRoundedIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={Boolean(novo)} onClose={() => setNovo(null)} fullWidth maxWidth="xs">
        <DialogTitle>{novo?.substituir ? `Substituir ${novo.substituir.nome}` : 'Adicionar à escala'}</DialogTitle>
        {novo && (
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {!novo.substituir && (
                <TextField select label="Função" value={novo.tipo} onChange={(e) => setNovo({ ...novo, tipo: e.target.value, pessoa: '', valor: '' })}>
                  <MenuItem value="goleiro">Goleiro</MenuItem>
                  <MenuItem value="arbitro">Árbitro</MenuItem>
                </TextField>
              )}
              <TextField
                select
                label={novo.tipo === 'goleiro' ? 'Goleiro' : 'Árbitro'}
                value={novo.pessoa}
                onChange={(e) => {
                  const escolhido = dados.disponiveis[novo.tipo].find((p) => p._id === e.target.value);
                  setNovo({ ...novo, pessoa: e.target.value, valor: escolhido ? escolhido.valorPorPelada || 0 : '' });
                }}
                helperText={opcoes.length === 0 ? 'Nenhum cadastro disponível. Cadastre em Administração › Árbitros e goleiros.' : undefined}
              >
                {opcoes.map((p) => (
                  <MenuItem key={p._id} value={p._id}>{p.nome} · {formatCurrency(p.valorPorPelada)}</MenuItem>
                ))}
              </TextField>
              <TextField label="Valor nesta pelada" type="number" value={novo.valor} onChange={(e) => setNovo({ ...novo, valor: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} helperText="Padrão: valor do cadastro" />
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNovo(null)} color="inherit">Cancelar</Button>
          <Button onClick={salvarNovo} variant="contained" disabled={ocupado || !novo?.pessoa}>{novo?.substituir ? 'Substituir' : 'Adicionar'}</Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export default EscalaPelada;
